import { GlIcon, GlDatepicker, GlTooltipDirective, GlLink, GlPopover } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { createAlert } from '../../../alert.js?vue3';
import HelpIcon from '../../../vue_shared/components/help_icon/help_icon.vue?vue3';
import { TYPE_ISSUE } from '~/issues/constants';
import { localeDateFormat, newDate, toISODateFormat } from '~/lib/utils/datetime_utility';
import { helpPagePath } from '~/lib/utils/path_helpers/help';
import { __, sprintf } from '~/locale';
import { dateFields, dateTypes, Tracking } from '../../constants';
import { dueDateQueries, startDateQueries } from '../../queries/constants';
import SidebarEditableItem from '../sidebar_editable_item.vue?vue3';
import SidebarFormattedDate from './sidebar_formatted_date.vue?vue3';
import SidebarInheritDate from './sidebar_inherit_date.vue?vue3';
export default {
    name: 'SidebarDateWidget',
    tracking: {
        event: Tracking.editEvent,
        label: Tracking.rightSidebarLabel
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    components: {
        GlIcon,
        GlDatepicker,
        GlLink,
        GlPopover,
        SidebarEditableItem,
        SidebarFormattedDate,
        SidebarInheritDate,
        HelpIcon
    },
    props: {
        iid: {
            type: String,
            required: true
        },
        fullPath: {
            type: String,
            required: true
        },
        dateType: {
            type: String,
            required: false,
            default: dateTypes.due
        },
        issuableType: {
            required: true,
            type: String
        },
        canInherit: {
            required: false,
            type: Boolean,
            default: false
        },
        minDate: {
            required: false,
            type: Date,
            default: null
        },
        maxDate: {
            required: false,
            type: Date,
            default: null
        }
    },
    data () {
        return {
            issuable: {},
            loading: false,
            tracking: {
                ...this.$options.tracking,
                property: this.dateType === dateTypes.start ? 'startDate' : 'dueDate'
            }
        };
    },
    apollo: {
        issuable: {
            query () {
                return this.dateQueries[this.issuableType].query;
            },
            variables () {
                return {
                    fullPath: this.fullPath,
                    iid: String(this.iid)
                };
            },
            skip () {
                return !this.iid;
            },
            update (data) {
                return data.namespace?.issuable || {};
            },
            error () {
                createAlert({
                    message: sprintf(__('Something went wrong while setting %{issuableType} %{dateType} date.'), {
                        issuableType: this.issuableType,
                        dateType: this.dateType === dateTypes.start ? 'start' : 'due'
                    })
                });
            },
            subscribeToMore: {
                document () {
                    return this.dateQueries[this.issuableType].subscription;
                },
                variables () {
                    return {
                        issuableId: this.issuableId
                    };
                },
                skip () {
                    return this.skipIssueDueDateSubscription;
                }
            }
        }
    },
    computed: {
        dateQueries () {
            return this.dateType === dateTypes.start ? startDateQueries : dueDateQueries;
        },
        dateLabel () {
            return this.dateType === dateTypes.start ? this.$options.i18n.startDate : this.$options.i18n.dueDate;
        },
        removeDateLabel () {
            return this.dateType === dateTypes.start ? this.$options.i18n.removeStartDate : this.$options.i18n.removeDueDate;
        },
        dateValue () {
            return this.issuable?.[this.dateType] || null;
        },
        isLoading () {
            return this.$apollo.queries.issuable.loading || this.loading;
        },
        initialLoading () {
            return this.$apollo.queries.issuable.loading;
        },
        hasDate () {
            return this.dateValue !== null;
        },
        parsedDate () {
            if (!this.hasDate) {
                return null;
            }
            return newDate(this.dateValue);
        },
        formattedDate () {
            if (!this.hasDate) {
                return this.$options.i18n.noDate;
            }
            return localeDateFormat.asDate.format(this.parsedDate);
        },
        workspacePath () {
            return this.issuableType === TYPE_ISSUE ? {
                projectPath: this.fullPath
            } : {
                groupPath: this.fullPath
            };
        },
        dataTestId () {
            return this.dateType === dateTypes.start ? 'sidebar-start-date' : 'sidebar-due-date';
        },
        issuableId () {
            return this.issuable.id;
        },
        skipIssueDueDateSubscription () {
            return this.issuableType !== TYPE_ISSUE || !this.issuableId || this.isLoading;
        }
    },
    methods: {
        epicDatePopoverEl () {
            return this.$refs?.epicDatePopover?.$el;
        },
        openDatePicker () {
            this.$refs.datePicker.show();
        },
        setFixedDate (isFixed) {
            const date = this.issuable[dateFields[this.dateType].dateFixed];
            this.setDate(date, isFixed);
        },
        setDate (date, isFixed = true) {
            const formattedDate = date ? toISODateFormat(date) : null;
            this.loading = true;
            this.$refs.editable.collapse();
            this.$apollo.mutate({
                mutation: this.dateQueries[this.issuableType].mutation,
                variables: {
                    input: {
                        ...this.workspacePath,
                        iid: this.iid,
                        ...this.canInherit ? {
                            [dateFields[this.dateType].dateFixed]: isFixed ? formattedDate : undefined,
                            [dateFields[this.dateType].isDateFixed]: isFixed
                        } : {
                            [this.dateType]: formattedDate
                        }
                    }
                }
            }).then(({ data: { issuableSetDate: { errors } } })=>{
                if (errors.length) {
                    createAlert({
                        message: errors[0]
                    });
                }
            }).catch(()=>{
                createAlert({
                    message: sprintf(__('Something went wrong while setting %{issuableType} %{dateType} date.'), {
                        issuableType: this.issuableType,
                        dateType: this.dateType === dateTypes.start ? 'start' : 'due'
                    })
                });
            }).finally(()=>{
                this.loading = false;
            });
        }
    },
    i18n: {
        dueDate: __('Due date'),
        startDate: __('Start date'),
        noDate: __('None'),
        removeDueDate: __('remove due date'),
        removeStartDate: __('remove start date'),
        dateHelpValidMessage: __('These dates affect how your epics appear in the roadmap. Set a fixed date or one inherited from the milestones assigned to issues in this epic.'),
        help: __('Help'),
        learnMore: __('Learn more')
    },
    dateHelpUrl: helpPagePath('user/group/epics/manage_epics.md', {
        anchor: 'start-and-due-date-inheritance'
    })
};
